import React, { useState } from 'react';

import { EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import RingCentralRecordMediaModal from 'components/MysteryShopping/RingCentralRecords/RingCentralRecordMediaModal';
import { type CommunicationTagConfig, getCommunicationTagConfig } from 'constants/communicationTags';
import { getFormattedDate } from 'helpers/dates';

import { ACTIVITY_DIRECTION_TYPE } from './constants';

type ActivityHeaderProps = {
  title: string;
  directionType: number;
  outcomeValue?: string | null;
  outcomeLabel?: string | null;
  actorName?: string | null;
  occurredAt: string;
  fromRecipientText: string | null;
  hasRenderedHtml: boolean;
  ringCentralRecordId?: number | null;
  showRenderedBody: boolean;
  onToggleRenderedBody: () => void;
};

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px 12px;
`;

const Left = styled.div`
  flex: 1 1 220px;
  min-width: 0;
`;

const SenderText = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  word-break: break-word;
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Title = styled.div`
  color: var(--text-primary);
  font-family: var(--font-bold);
  font-size: 14px;
  word-break: break-word;
`;

const ActivityTag = styled.span<{ $tagConfig: CommunicationTagConfig }>`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  width: fit-content;
  padding: 1px 6px;
  border: 1px solid ${({ $tagConfig }) => `var(${$tagConfig.borderColor})`};
  border-radius: 999px;
  background: ${({ $tagConfig }) => `var(${$tagConfig.backgroundColor})`};
  color: ${({ $tagConfig }) => `var(${$tagConfig.textColor})`};
  font-size: 10px;
  overflow-wrap: anywhere;
  white-space: nowrap;
`;

const ActivityTagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
`;

const ViewRecordTag = styled.button`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-height: 16px;
  padding: 1px 6px;
  border: 0;
  border-radius: 4px;
  background: var(--background-gray);
  color: var(--dark-blue);
  cursor: pointer;
  font-family: var(--font-bold);
  font-size: 10px;
  overflow-wrap: anywhere;

  &:hover {
    background: var(--background-gray-light);
  }
`;

const formatOutcomeLabel = (outcomeLabel: string) => outcomeLabel.replaceAll('—', '-');

const DIRECTION_LABELS: Record<number, string> = {
  [ACTIVITY_DIRECTION_TYPE.INBOUND]: 'Inbound',
  [ACTIVITY_DIRECTION_TYPE.OUTBOUND]: 'Outbound',
  [ACTIVITY_DIRECTION_TYPE.UNKNOWN]: 'Unknown',
  [ACTIVITY_DIRECTION_TYPE.INTERNAL]: 'Internal',
};

const DIRECTION_TAG_VALUES: Record<number, string> = {
  [ACTIVITY_DIRECTION_TYPE.INBOUND]: 'inbound',
  [ACTIVITY_DIRECTION_TYPE.OUTBOUND]: 'outbound',
  [ACTIVITY_DIRECTION_TYPE.UNKNOWN]: 'unknown',
  [ACTIVITY_DIRECTION_TYPE.INTERNAL]: 'unknown',
};

const Meta = styled.div`
  color: var(--text-secondary);
  font-size: 11px;
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 1 auto;
  min-width: 0;

  @media (max-width: 640px) {
    flex-basis: 100%;
  }
`;

const ViewToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--background-gray);
    color: var(--text-primary);
  }
`;

const ActivityHeader = ({
  title,
  directionType,
  outcomeValue,
  outcomeLabel,
  actorName,
  occurredAt,
  fromRecipientText,
  hasRenderedHtml,
  ringCentralRecordId,
  showRenderedBody,
  onToggleRenderedBody,
}: ActivityHeaderProps) => {
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const occurredAtText = getFormattedDate(occurredAt, 'MMM D, YYYY h:mm A');
  const metaText = actorName ? `${actorName}, ${occurredAtText}` : occurredAtText;
  const formattedOutcomeLabel = outcomeLabel ? formatOutcomeLabel(outcomeLabel) : null;

  return (
    <Container>
      <Left>
        <TitleRow>
          <Title>{title}</Title>
          <ActivityTagGroup>
            {formattedOutcomeLabel && (
              <ActivityTag $tagConfig={getCommunicationTagConfig(outcomeValue)}>{formattedOutcomeLabel}</ActivityTag>
            )}
            <ActivityTag $tagConfig={getCommunicationTagConfig(DIRECTION_TAG_VALUES[directionType])}>
              {DIRECTION_LABELS[directionType] || 'Unknown'}
            </ActivityTag>
          </ActivityTagGroup>
          {ringCentralRecordId && (
            <ViewRecordTag type="button" onClick={() => setIsRecordModalOpen(true)}>
              View record
            </ViewRecordTag>
          )}
        </TitleRow>
        {fromRecipientText && <SenderText>{fromRecipientText}</SenderText>}
      </Left>
      <HeaderActions>
        <Meta>{metaText}</Meta>
        {hasRenderedHtml && (
          <ViewToggleButton
            onClick={onToggleRenderedBody}
            title={showRenderedBody ? 'Show plain text' : 'Show rendered message'}
            type="button"
          >
            {showRenderedBody ? <FileTextOutlined /> : <EyeOutlined />}
          </ViewToggleButton>
        )}
      </HeaderActions>
      {ringCentralRecordId && (
        <RingCentralRecordMediaModal
          recordId={isRecordModalOpen ? ringCentralRecordId : null}
          onClose={() => setIsRecordModalOpen(false)}
        />
      )}
    </Container>
  );
};

export default ActivityHeader;
